import { format } from "date-fns";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePickerInput } from "@/components/ui/DatePickerInput";
import { useGetPeriodOptionsQuery } from "@/services/(company)/payrollRunsApi";
import type { ScopeItem } from "./SelectScope";

export interface RunConfig {
  period: string;
  payDate: string;
  payrollDate: string;
  name: string;
  notes: string;
}

interface IProps {
  branchId: number | null;
  selected: ScopeItem[];
  config: RunConfig;
  onChange: (next: RunConfig) => void;
}

const toIsoDate = (value?: string) => (value ? value.slice(0, 10) : "");

const parseIsoDate = (value: string): Date | undefined => {
  if (!value) return undefined;
  const d = new Date(`${value}T00:00:00`);
  return isNaN(d.getTime()) ? undefined : d;
};

const dateToIso = (d: Date | undefined): string =>
  d ? format(d, "yyyy-MM-dd") : "";

export const ConfigureRun = ({
  branchId,
  selected,
  config,
  onChange,
}: IProps) => {
  const { t } = useTranslation(["company", "common"]);
  const update = (patch: Partial<RunConfig>) =>
    onChange({ ...config, ...patch });

  // Pass the selected scope so the period lock is scope-aware: a month is
  // only "already run" once every employee in this scope is covered.
  const departmentIds = selected
    .filter((s) => s.kind === "department")
    .map((s) => Number(s.id.replace("dept-", "")));
  const payrollGroupIds = selected
    .filter((s) => s.kind === "group")
    .map((s) => Number(s.id.replace("grp-", "")));

  const { data: periodResponse, isLoading: isLoadingPeriods } =
    useGetPeriodOptionsQuery(
      { branchId: branchId!, departmentIds, payrollGroupIds },
      { skip: !branchId },
    );
  const periodOptions = periodResponse?.data?.periods ?? [];

  const branchName =
    selected.find((s) => s.kind === "branch")?.name ??
    selected.find((s) => s.parentBranchId)?.name ??
    "—";

  const totalEmployees =
    selected.find((s) => s.kind === "branch")?.employees ??
    selected.reduce((acc, s) => acc + s.employees, 0);

  const handlePeriodChange = (periodKey: string) => {
    const opt = periodOptions.find((p) => p.periodKey === periodKey);
    // Prefer overrides saved via Save Changes; fall back to the policy's
    // scheduled dates for fresh periods.
    const payDate =
      toIsoDate(opt?.actualPayDate ?? opt?.scheduledPayDate) || config.payDate;
    const payrollDate =
      toIsoDate(opt?.actualPayrollDate ?? opt?.scheduledPayrollDate) ||
      config.payrollDate;
    onChange({
      ...config,
      period: periodKey,
      payDate,
      payrollDate,
      name: opt?.savedName ?? "",
      notes: opt?.savedNotes ?? "",
    });
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Selected chips (read-only) */}
      <div className="rounded-xs bg-gray-50 px-4 py-3 flex flex-wrap gap-2">
        {selected.map((s) => (
          <span
            key={s.id}
            className="inline-flex items-center gap-2 rounded-xs border border-primary bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary"
          >
            {s.name}
          </span>
        ))}
      </div>

      {/* Form */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
        <div className="flex flex-col gap-1.5">
          <label className="block text-xs text-border-strong font-medium uppercase">
            {t("company:payrolls.payroll_period")}
          </label>
          <Select
            value={config.period}
            onValueChange={handlePeriodChange}
            disabled={!branchId || isLoadingPeriods}
          >
            <SelectTrigger className="h-12">
              <SelectValue
                placeholder={
                  isLoadingPeriods
                    ? t("common:states.loading")
                    : t("company:payrolls.select_period")
                }
              />
            </SelectTrigger>
            <SelectContent>
              {periodOptions.map((p) => {
                const covered = p.coveredCount ?? 0;
                const scopeTotal = p.scopeCount ?? 0;
                // Partial overlap: some (not all) scoped employees already run.
                const isPartial =
                  !p.isGenerated && covered > 0 && covered < scopeTotal;
                return (
                  <SelectItem
                    key={p.periodKey}
                    value={p.periodKey}
                    disabled={p.isGenerated}
                  >
                    {p.periodLabel}
                    {p.isGenerated && ` ${t("company:payrolls.already_run")}`}
                    {isPartial &&
                      ` ${t("company:payrolls.already_run_partial", {
                        covered,
                        total: scopeTotal,
                      })}`}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <DatePickerInput
          label={t("company:payrolls.payroll_date")}
          value={parseIsoDate(config.payrollDate)}
          onChange={(d) => update({ payrollDate: dateToIso(d) })}
        />

        <DatePickerInput
          label={t("company:payrolls.pay_date")}
          value={parseIsoDate(config.payDate)}
          onChange={(d) => update({ payDate: dateToIso(d) })}
        />

        <div className="flex flex-col gap-1.5">
          <label className="block text-xs text-border-strong font-medium uppercase">
            {t("company:payrolls.run_name")}
          </label>
          <input
            type="text"
            value={config.name}
            onChange={(e) => update({ name: e.target.value })}
            placeholder={t("company:payrolls.ph_payroll_run_name")}
            className="h-12 border border-input rounded-xs px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring"
          />
        </div>

        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <label className="block text-xs text-border-strong font-medium uppercase">
            {t("common:labels.notes")}
          </label>
          <textarea
            value={config.notes}
            onChange={(e) => update({ notes: e.target.value })}
            placeholder={t(
              "company:payrolls.optional_notes_for_this_payroll_run",
            )}
            rows={3}
            className="border border-input rounded-xs px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring resize-none"
          />
        </div>
      </div>

      {/* Run summary */}
      <div className="rounded-xs border border-border bg-white mt-auto">
        <div className="px-4 py-3 border-b border-border bg-gray-50">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            {t("company:payrolls.run_summary")}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-4 px-4 py-4">
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              {t("company:payrolls.scope")}
            </span>
            <span className="text-sm font-semibold text-gray-900">
              {branchName}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              {t("company:payrolls.employees")}
            </span>
            <span className="text-sm font-semibold text-gray-900">
              {totalEmployees}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              {t("company:payrolls.pay_date")}
            </span>
            <span className="text-sm font-semibold text-gray-900">
              {config.payDate || "—"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
import { useTranslation } from "react-i18next";
